// components/layout/Sidebar.tsx
import { useDarkTheme } from "@/contexts/DarkTheme";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { useIsMobile } from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@react-hook/media-query";
import { ChartNoAxesGantt, Gauge, Rocket, Unplug } from "lucide-react";
import { signOut } from "next-auth/react";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState, ReactNode, useRef } from "react";
import { BsBarChart } from "react-icons/bs";
import { IoCreateOutline } from "react-icons/io5";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "../../ui/menubar";
import {
  brokerConnectModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import { useRecoilState } from "recoil";
import { motion, AnimatePresence } from "framer-motion";
import { SidebarCarousel } from "../common/SideBarCarousal";
import { useNotificationSocket } from "@/contexts/NotificationSocketContext";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { useUserContext } from "@/contexts/userContext";
import { Button } from "@/components/ui/button";
import Hamburger from "hamburger-react";

interface SidebarItemProps {
  href?: string;
  icon: ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

function SidebarItem({ href, icon, label, active, onClick }: SidebarItemProps) {
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const { t } = useTranslation("layout-page");
  const isMobile = useIsMobile(false);
  const handleClick = () => {
    if (isMobile) setShowSidebar(false);
    onClick?.();
  };

  const content = (
    <li
      onClick={handleClick}
      className={cn(
        "flex gap-3 items-center  text-sm rounded-[6px] p-2 pl-5 capitalize cursor-pointer hover:bg-primary hover:text-primary-foreground",
        active && "bg-primary text-primary-foreground",
        !showSidebar && "justify-start ",
      )}
    >
      <div className="flex items-center justify-center w-5 h-5 shrink-0">
        {icon}
      </div>
      <motion.div
        animate={{
          opacity: showSidebar ? 1 : 0,
          width: showSidebar ? "auto" : 0,
        }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden whitespace-nowrap"
      >
        {label}
      </motion.div>
    </li>
  );

  return href ? (
    <Link href={href} className="w-full">
      <HoverCard openDelay={0}>
        <HoverCardTrigger>{content}</HoverCardTrigger>
        {!showSidebar && (
          <HoverCardContent className="w-auto ml-[52px] mt-[-20px] p-2 font-medium z-[1000]">
            {label}
          </HoverCardContent>
        )}
      </HoverCard>
    </Link>
  ) : (
    <HoverCard openDelay={0}>
      <HoverCardTrigger>{content}</HoverCardTrigger>
      {!showSidebar && (
        <HoverCardContent className="w-auto ml-[52px] mt-[-20px] p-2 font-medium z-[1000]">
          {t(label)}
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

export default function SideBar({ showDisabled }: { showDisabled?: boolean }) {
  const { t } = useTranslation("layout-page");
  const { pathname, query, push, reload } = useRouter();
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const { darkTheme } = useDarkTheme();
  const matchesMobile = useMediaQuery(`only screen and (max-width:  1024px)`);
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom,
  );
  const { user } = useUserContext();

  const isMobile = useIsMobile(false);
  const { socket } = useNotificationSocket();
  const [headerHeight, setHeaderHeight] = useState(0);
  const rafRef = useRef<number>();
  const { details } = useAppDetails();

  useEffect(() => {
    const header = document.getElementById("main-header");
    if (!header) return;

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setHeaderHeight(header.offsetHeight + 5);
      });
    });

    observer.observe(header);
    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Popup logic
  const [showPopup, setShowPopup] = useState(false);
  useEffect(() => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("changelog_v1="))
      ?.split("=")[1];
    if (cookieValue !== "1") setShowPopup(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      showSidebar && matchesMobile ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showSidebar, matchesMobile]);

  useEffect(() => {
    if (matchesMobile) {
      setShowSidebar(false);
    }
  }, [matchesMobile]);

  const Logout = () => {
    socket?.close();
    Object.keys(localStorage).forEach((key) => {
      if (key !== "app-details") {
        localStorage.removeItem(key);
      }
    });
    signOut({ redirect: false });
    push({ pathname: "/logout", query }).then(() => reload());
  };

  return (
    <>
      {/* Sidebar */}
      <motion.nav
        initial={{ x: matchesMobile ? -200 : 0, width: showSidebar ? 222 : 70 }}
        animate={{
          x: showSidebar ? 0 : matchesMobile ? -200 : 0,
          width: showSidebar ? 222 : 70,
        }}
        transition={{ type: "spring", stiffness: 250, damping: 30 }}
        className={cn(
          "p-2 lg:block fixed lg:sticky z-20",
          darkTheme ? "bg-[#081121] shadow-md" : "bg-white",
          showDisabled &&
            "hidden lg:block lg:pointer-events-none lg:opacity-50",
          isMobile && "h-full ",
        )}
        style={{ top: `${headerHeight}px` }}
      >
        <div
          className="flex flex-col justify-between "
          style={{ height: `calc(100vh - ${headerHeight + 15}px)` }}
        >
          <div>
            <Button
              onClick={() => setShowSidebar(!showSidebar)}
              variant="outline"
              className="px-0 flex items-center justify-center hidden lg:flex"
            >
              <Hamburger
                size={15}
                toggled={showSidebar}
                toggle={setShowSidebar}
              />
            </Button>
            {/* Sidebar Menu */}
            <ul className="flex flex-col gap-2 cursor-pointer w-full mt-5">
              <SidebarItem
                href={`/build?locale=${query.locale || "en"}`}
                icon={<IoCreateOutline size={15} />}
                label={t("Create")}
                active={pathname.includes("build")}
              />
              <SidebarItem
                href={`/test?locale=${query.locale || "en"}`}
                icon={<Gauge size={15} />}
                label={t("Test")}
                active={
                  pathname === "/test" || pathname.includes("strategy_details")
                }
              />
              <SidebarItem
                href={`/deploy?locale=${query.locale || "en"}`}
                icon={<Rocket size={15} />}
                label={t("Deploy")}
                active={
                  pathname.includes("deploy") || pathname.includes("forward")
                }
              />
              {details?.whiteLableOptions?.brokerConnectionModal !==
                "hidden" && (
                <SidebarItem
                  icon={<Unplug size={15} />}
                  label={t("Connect API")}
                  onClick={() => setShowConnectForm({ isOpen: true })}
                />
              )}

              <SidebarItem
                href={`/marketplace?locale=${query.locale || "en"}`}
                icon={<BsBarChart size={15} />}
                label={t("Marketplace")}
                active={pathname.includes("marketplace")}
              />

              {/* More Menu */}
              <Menubar className="flex flex-col h-fit border-none p-0 items-start w-full justify-start">
                <MenubarMenu>
                  <MenubarTrigger
                    className={cn(
                      "flex w-full p-2  pl-5 gap-5 items-center text-sm rounded-[6px] hover:bg-primary hover:text-primary-foreground",
                      !showSidebar && "justify-start ",
                    )}
                  >
                    <ChartNoAxesGantt size={15} />
                    {showSidebar && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="transition-opacity duration-200 ease-in-out"
                      >
                        {t("More")}
                      </motion.span>
                    )}
                  </MenubarTrigger>
                  <MenubarContent>
                    <Link href="/test?tab=scanners">
                      <MenubarItem>{t("Scanners")}</MenubarItem>
                    </Link>
                    {!details?.isWhiteLabled && (
                      <>
                        <Link href="/subscription">
                          <MenubarItem>{t("Manage Subscription")}</MenubarItem>
                        </Link>

                        <Link href="/broker-stats-v2">
                          <MenubarItem>{t("Broker Dashboard")}</MenubarItem>
                        </Link>

                        <Link
                          href="https://www.youtube.com/@trylevel2/videos"
                          target="_blank"
                        >
                          <MenubarItem>{t("Explainer Videos")}</MenubarItem>
                        </Link>
                      </>
                    )}
                    {user?.is_admin && (
                      <MenubarItem
                        onClick={() =>
                          push(
                            `/admin/dashboard?locale=${query.locale || "en"}`,
                          )
                        }
                      >
                        {t("Admin Dashboard")}
                      </MenubarItem>
                    )}
                    <MenubarItem
                      onClick={() =>
                        setShowTechnicalRequest({
                          isOpen: true,
                          isRequest: false,
                          isContactSales: false,
                          helpline: true,
                        })
                      }
                    >
                      {t("Technical support")}
                    </MenubarItem>
                    <MenubarItem onClick={Logout}>{t("Logout")}</MenubarItem>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </ul>
          </div>

          {/* Footer */}
          <AnimatePresence>
            {showSidebar && <SidebarCarousel />}
          </AnimatePresence>
        </div>
      </motion.nav>
    </>
  );
}
